import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LinkedCircles, DotGrid } from "@/components/Shapes";

// About Me — content verbatim from Leah Moschella brand PDF
export default function About() {
    return (
        <div className="pt-32 md:pt-40 pb-16" data-testid="about-page">
            <section className="mx-auto max-w-[1280px] px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
                    <div className="lg:col-span-6 relative">
                        <LinkedCircles size={64} color="orange" stroke={1.5} className="mb-4" />
                        <p className="overline">About</p>
                        <h1 className="mt-4 text-3xl md:text-5xl font-bold text-[rgb(var(--teal))] tracking-tight">
                            Hi, I&apos;m <span className="italic text-[rgb(var(--orange))]">Leah.</span>
                        </h1>
                        <p className="mt-6 text-lg italic text-[rgb(var(--ink-2))] max-w-xl">
                            I started writing because I kept thinking, <em>I can&apos;t be the only one.</em>
                        </p>
                    </div>
                    {/* The four-mic keynote collage — Leah in her natural habitat */}
                    <div className="lg:col-span-6 relative">
                        <div className="absolute -bottom-4 -right-4 hidden md:block z-0">
                            <DotGrid rows={4} cols={4} gap={12} size={2.5} color="teal" />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, scale: 1.03 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="frame aspect-square relative z-10"
                        >
                            <img
                                src="https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/wsxyftc0_lnmp7.jpg"
                                alt="Leah on stage, four frames of a keynote — mic in hand, hand in the air, unimpressed, and back at it."
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            <div className="mx-auto max-w-[1280px] px-6 md:px-10 mt-10">
                <div className="rule" />
            </div>

            <section className="mx-auto max-w-[1280px] px-6 md:px-10 py-12 md:py-16">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-4">
                        <p className="overline">Note from Leah</p>
                        <div className="mt-3 rule-orange" />
                    </div>
                    <div className="md:col-span-8 prose-editorial max-w-2xl">
                        <p>
                            Part therapy. Part public service announcement. Mostly an attempt to make sure you don&apos;t
                            make the same mistakes I did.
                        </p>
                        <p>
                            One time I deeply resented a volunteer for doing the wildly irresponsible act of their
                            laundry. Eventually, I had no clean underwear and was buying an overpriced emergency pair.
                            My own life somehow became an optional project. I&apos;m learning and unlearning that the
                            world will not be saved wearing dirty underwear.
                        </p>
                        <p>
                            For a long time, I chased accolades. Like any good millennial, someone needed to give me
                            a trophy. I agonized over the next resume bullet and was sure to make sure the entirety of
                            Linkedin knew that I had heard that inspirational quote at that conference keynote.
                        </p>
                        <p>
                            I optimized my mornings, maximized my KPIs, scheduled emails for 4:30 a.m., and proudly
                            climbed the ladder. All while volunteering to plan the team ice cream social to prove that
                            I was a team builder and a valuable player.
                        </p>
                        <p>
                            Prove to who? That was the question. It took me too long to realize that some white dude
                            can no longer be the one to validate me. (He&apos;s too busy presenting the idea from your
                            4:30 am email as his own.)
                        </p>
                        <p>
                            I&apos;ve always needed proof that I was smart enough, successful enough, liked enough.
                        </p>
                        <p>
                            <figure className="float-right ml-6 mb-3 w-40 md:w-52 not-italic">
                                <div className="frame aspect-square">
                                    <img
                                        src="https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/8gaf8wfz_PXL_20250915_195322509~2.webp"
                                        alt="Leah and her calico cat, both looking directly at the camera."
                                    />
                                </div>
                                <figcaption className="mt-2 text-[12px] italic text-[rgb(var(--ink-2))]/80 leading-snug">
                                    The co-founder.
                                </figcaption>
                            </figure>
                            If I&apos;m being honest, it terrifies me that my &ldquo;About Me&rdquo; isn&apos;t a
                            litany of fellowships, awards, and titles. This is the first time in my 40+ years that
                            I&apos;m telling the world that sometimes I&apos;d rather eat KFC while watching 90 Day
                            Fiance. Hi I&apos;m Leah, and I wish my Linkedin title was &ldquo;Stay-at-Home Cat Mom.&rdquo;
                        </p>
                        <p>
                            <figure className="float-left mr-6 mb-3 w-40 md:w-52 not-italic">
                                <div className="frame aspect-[3/4]">
                                    <img
                                        src="https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/qrnmwu3i_PXL_20241220_150154562_exported_738_1734709549776.webp"
                                        alt="Leah in Reykjavík, mid-hot-dog, delighted."
                                    />
                                </div>
                                <figcaption className="mt-2 text-[12px] italic text-[rgb(var(--ink-2))]/80 leading-snug">
                                    Reykjavík. Hot dog. No regrets.
                                </figcaption>
                            </figure>
                            At my 42nd birthday party, I had each of the 20 guests give me a challenge of something
                            new to learn or accomplish. I didn&apos;t drive and yet in a year I had to find a way to
                            drive a Lamborghini. I know nothing about coding, but I vibed my first video game about
                            Mrs. Chippy, the cat who sailed to Antarctica with Shackleton. (My version had a happier fate.)
                        </p>
                        <p>
                            This is a site dedicated to my learning, unlearning, and discovery. What does it mean to
                            be a 40-something white American lady who is finally ready to validate herself (or admit
                            that hi, I&apos;m the problem, it&apos;s me.) So here I am with the detours and plot
                            twists that have taught me more than perfection ever will.
                        </p>
                        <blockquote>
                            I used to chase trophies because I thought they&apos;d prove I&apos;d arrived.
                            <br />
                            Now I&apos;m chasing curiosity because it keeps reminding me there&apos;s somewhere new to go.
                        </blockquote>
                        <p>
                            Maybe there&apos;s a trophy for telling the world my journey, or maybe, if I&apos;m lucky,
                            you&apos;ll recognize yourself somewhere in the margins.
                        </p>
                    </div>
                </div>
            </section>

            <div className="mx-auto max-w-[1280px] px-6 md:px-10">
                <div className="rule" />
            </div>

            <section className="mx-auto max-w-[1280px] px-6 md:px-10 py-12 md:py-16">
                <div className="flex flex-wrap gap-3">
                    <Link to="/contact" className="btn-primary" data-testid="about-contact-cta">
                        Let&apos;s create together →
                    </Link>
                    <Link to="/#collections" className="btn-ghost" data-testid="about-essays-cta">
                        Read the stories →
                    </Link>
                </div>
            </section>
        </div>
    );
}
